import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { Box, Text } from "@cruk/cruk-react-components";

import { fwsUrlTeamMembers } from "@fwa/src/services/teamService";
import { fwsUrlPageDefaultHeaderImages } from "@fwa/src/services/fundraisingPageService";
import { CollapsableSection } from "@fwa/src/components/CollapsableSection";
import {
  ProgressList,
  completionStats,
} from "@fwa/src/components/ProgressList";

import sharedStyles from "@fwa/src/components/styles.module.css";

import {
  type TeamPageType,
  type ImageType,
  type FwsResponseData,
  type ProgressItemType,
} from "@fwa/src/types";

export function TeamPageProgress({ page }: { page: TeamPageType }) {
  const membersUrl = fwsUrlTeamMembers({
    teamId: page.uniqueId,
    currentPage: 1,
    itemsPerPage: 2,
    sort: "leader",
  });
  const { totalItems } = useSwrGet(membersUrl, {});

  const headerImageUrl = page
    ? fwsUrlPageDefaultHeaderImages({
        pageId: page.uniqueId,
      })
    : null;
  const { data: headerImagesData } =
    useSwrGet<FwsResponseData<ImageType>>(headerImageUrl);
  const headerImages = headerImagesData?.results || [];

  const isDefaultHeader =
    page.headerImage &&
    headerImages.find(
      (image: ImageType) => image.uniqueId === page.headerImage?.uniqueId,
    );

  const hasStory = !!page?.story?.length;
  const hasImages = !isDefaultHeader;
  const hasMembers = totalItems > 1;

  const progressItems: ProgressItemType[] = [
    {
      label: "Set up your team page",
      isDone: true,
    },
    {
      label: `Personalise your team's story`,
      fieldName: "story",
      dataCtaType: "progress-edit-story",
      isDone: hasStory,
    },
    {
      label: `Add your team's cover image`,
      fieldName: "headerImage",
      dataCtaType: "progress-edit-header-image",
      isDone: hasImages,
    },
    {
      label: "Get team members to join",
      fieldName: "members",
      dataCtaType: "progress-invite-to-team",
      isDone: hasMembers,
    },
  ];

  const { complete } = completionStats(progressItems);

  return (
    <Box data-component="team-page-progress">
      <div className={sharedStyles["top-border-chunky"]}>
        <Box
          backgroundColor="background-light"
          padding="s"
          paddingBottom="none"
        >
          {headerImagesData && (
            <CollapsableSection
              id="page-progress"
              headerTitleText="Personalise your Team Page"
              startOpen={!complete}
            >
              <Box paddingBottom="s">
                <Text>
                  {complete
                    ? "Nice work - your Team page is looking great!"
                    : "Every story matters. Use these quick links to help tell yours."}
                </Text>
                <div className={sharedStyles["row-wrap"]}>
                  <ProgressList progressItems={progressItems} page={page} />
                </div>
              </Box>
            </CollapsableSection>
          )}
        </Box>
      </div>
    </Box>
  );
}

export default TeamPageProgress;
